import { TooltipDirective } from '../../../../../auxiliares/tooltip/tooltip.directive';
import { BOTONES } from '../../../../../componentes/botones';
import { DecimalPipe } from '@angular/common';
import {
  Component,
  DestroyRef,
  ViewEncapsulation,
  computed,
  inject,
  resource,
  signal,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { ActivatedRoute, RouterLink } from '@angular/router';
import * as Highcharts from 'highcharts';
import {
  IConfigDeseada,
  IDownlinkJob,
  IListado,
  ILuminaria,
  IUsuario,
} from 'modelos/src';
import { ConfigsDeseadasService } from '../../../../../auxiliares/servicios/http/configs-deseadas.service';
import { DownlinkJobsService } from '../../../../../auxiliares/servicios/http/downlink-jobs.service';
import { LuminariasHttpService } from '../../../../../auxiliares/servicios/http/luminarias.service';
import { HelperService } from '../../../../../auxiliares/servicios/helper.service';
import { ChartColors } from '../../../../../auxiliares/servicios/chart-colors.service';
import { BreadcrumbComponent } from '../../../../../componentes/breadcrumb/breadcrumb.component';
import { ChartCardComponent } from '../../../../../componentes/chart-card/chart-card.component';
import { LoginService } from '../../../../login/login.service';
import {
  NombresSeguimientoService,
  nombreUsuario,
} from '../nombres-seguimiento.service';
import {
  CADENCIA,
  DiferenciasExplicadas,
  MOTIVOS_APLICACION,
  MOTIVOS_FALTA,
  MotivoFalta,
  SELECT_JOBS_APLICACION,
  TONO,
  clienteSinMarca,
  curvaAvance,
  distribucionDemoras,
  duracion,
  enTramos,
  esSinMarca,
  estadoAplicacion,
  explicarDiferencias,
  fechaHora,
  haceCuanto,
  hitosConvergencia,
  momentosLlegada,
  motivoFalta,
  ordenFalta,
  queSeHizo,
  ritmoLlegada,
  rutaFuente,
  textoFuente,
} from '../seguimiento.helpers';
import type {
  DatosInformeAplicacion,
  FilaFalta,
} from './informe-aplicacion.modelo';

/** Tope de deseadas de una acción: una por nodo. */
const TOPE = 20000;

/** Cuántas luminarias que faltan se listan sin filtro. Con filtro, todas las del motivo. */
const TOPE_TABLA = 100;

/** Cuántas luminarias que faltan entran en el informe PDF: unas cien páginas. */
const TOPE_INFORME = 3000;

const SELECT_DESEADA =
  '_id idEntidad idCliente tipo estado diffs bloqueadoHasta reintentosReconciliacion ' +
  'fechaAplicacion ultimaComparacion aplicacion';

const POPULATE_DISPOSITIVO = JSON.stringify([
  {
    path: 'dispositivo',
    select: '_id deveui tipo fechaUltimaComunicacion tiempoLimiteComunicacion',
  },
]);

/** Las marcas de un eje de fechas en 24 h y con números. */
const FORMATOS_FECHA_EJE: Highcharts.AxisDateTimeLabelFormatsOptions = {
  millisecond: '%H:%M:%S',
  second: '%H:%M:%S',
  minute: '%H:%M',
  hour: '%H:%M',
  day: '%e/%m',
  week: '%e/%m',
  month: '%m/%Y',
  year: '%Y',
};

interface Falta {
  cd: IConfigDeseada;
  motivo: MotivoFalta;
  deveui?: string;
  idDispositivo?: string;
  luminaria?: ILuminaria;
  /** Qué le falta, dicho para el operador. */
  diferencias: DiferenciasExplicadas;
  ultimo?: IDownlinkJob;
}

/**
 * El detalle de una aplicación de perfil: cuántas luminarias ya tienen el
 * perfil, cómo fue llegando y **por qué faltan las que faltan**.
 *
 * La detenida agotó los intentos y hay que mirarle el nodo; la que confirmó y
 * no aplicó, la configuración; la que no comunica, ir a verla. Las que están en
 * curso sólo hay que esperarlas.
 *
 * El avance sale de la configuración deseada de cada nodo, no de los envíos: el
 * perfil se manda de a tandas y lo que importa es que quede puesto.
 */
@Component({
  selector: 'app-detalle-aplicacion',
  standalone: true,
  imports: [
    TooltipDirective,
    DecimalPipe,
    BOTONES,
    MatIconModule,
    MatTooltipModule,
    RouterLink,
    BreadcrumbComponent,
    ChartCardComponent,
  ],
  templateUrl: './detalle-aplicacion.component.html',
  styleUrl: '../seguimiento.scss',
  encapsulation: ViewEncapsulation.None,
})
export class DetalleAplicacionComponent {
  private readonly deseadasService = inject(ConfigsDeseadasService);
  private readonly jobsService = inject(DownlinkJobsService);
  private readonly luminariasService = inject(LuminariasHttpService);
  private readonly nombres = inject(NombresSeguimientoService);
  private readonly helper = inject(HelperService);
  private readonly ruta = inject(ActivatedRoute);

  /** Sin permiso para ver dispositivos LoRaWAN, el nodo va sin enlace. */
  protected readonly verNodos = this.helper.tienePermiso(
    'Administración - Ver dispositivos Lorawan',
  );

  protected readonly MOTIVOS_FALTA = MOTIVOS_FALTA;

  private readonly params = toSignal(this.ruta.paramMap, {
    initialValue: undefined,
  });
  protected readonly id = computed(() => this.params()?.get('id') ?? '');
  protected readonly sinMarca = computed(() => esSinMarca(this.id()));

  protected readonly motivoFiltro = signal<MotivoFalta | ''>('');
  protected readonly verSinComunicacion = signal(false);
  protected readonly generandoInforme = signal(false);

  constructor() {
    // Polleo con reload(): un cambio en los params vaciaría el detalle.
    const intervalo = setInterval(() => {
      if (document.visibilityState === 'visible') this.deseadas.reload();
    }, CADENCIA.detalle * 1000);
    inject(DestroyRef).onDestroy(() => clearInterval(intervalo));
  }

  protected readonly deseadas = resource({
    params: () => ({ id: this.id() }),
    loader: ({ params }): Promise<IListado<IConfigDeseada> | undefined> => {
      if (!params.id) return Promise.resolve(undefined);
      const filtro = esSinMarca(params.id)
        ? {
            'aplicacion.id': { $exists: false },
            estado: { $ne: 'Coincide' },
            idCliente: clienteSinMarca(params.id),
          }
        : { 'aplicacion.id': params.id };
      return this.deseadasService.getFiltered({
        filter: JSON.stringify(filtro),
        populate: POPULATE_DISPOSITIVO,
        select: SELECT_DESEADA,
        limit: TOPE,
        includeChildren: true,
        excludeTotalCount: true,
      });
    },
  });

  private readonly lista = computed(() => this.deseadas.value()?.datos ?? []);

  /** La marca de la acción: es la misma en todas sus deseadas. */
  protected readonly aplicacion = computed(
    () => this.lista().find((cd) => cd.aplicacion)?.aplicacion,
  );

  // Los params de las consultas derivadas son una CLAVE string: se comparan por
  // igualdad, así la recarga periódica no las vuelve a disparar (ni las vacía)
  // cuando las luminarias que faltan siguen siendo las mismas.

  /** El último envío de cada nodo que falta, desde que arrancó la acción. */
  private readonly ultimos = resource({
    params: () => {
      const desde = this.aplicacion()?.fecha ?? '';
      const ids = this.lista()
        .filter((cd) => cd.estado !== 'Coincide' && cd.idEntidad)
        .map((cd) => cd.idEntidad)
        .sort();
      return `${desde}|${ids.join(',')}`;
    },
    loader: async ({ params }) => {
      const [desde, lista] = params.split('|');
      const ids = lista ? lista.split(',') : [];
      const mapa = new Map<string, IDownlinkJob>();
      if (!desde || !ids.length) return mapa;
      for (const tramo of enTramos(ids)) {
        const res = await this.jobsService
          .getFiltered({
            filter: JSON.stringify({
              idDispositivoLorawan: { $in: tramo },
              fechaCreacion: { $gte: desde },
            }),
            sort: '-fechaCreacion',
            select: SELECT_JOBS_APLICACION,
            limit: tramo.length * 20,
            includeChildren: true,
            excludeTotalCount: true,
          })
          .catch((): undefined => undefined);
        for (const j of res?.datos ?? []) {
          const idDisp = j.idDispositivoLorawan;
          // Viene del más nuevo al más viejo: el primero de cada nodo es el último.
          if (idDisp && !mapa.has(idDisp)) mapa.set(idDisp, j);
        }
      }
      return mapa;
    },
  });

  /** Las luminarias de los nodos que faltan, para nombrarlas y enlazarlas. */
  private readonly luminarias = resource({
    params: () =>
      this.lista()
        .filter((cd) => cd.estado !== 'Coincide')
        .map((cd) => cd.dispositivo?.deveui)
        .filter((d): d is string => !!d)
        .sort()
        .join(','),
    loader: async ({ params }) => {
      const mapa = new Map<string, ILuminaria>();
      for (const tramo of enTramos(params ? params.split(',') : [])) {
        const res = await this.luminariasService
          .getFiltered({
            filter: JSON.stringify({ deveui: { $in: tramo } }),
            select: '_id identificacion deveui',
            limit: tramo.length,
            includeChildren: true,
            excludeTotalCount: true,
          })
          .catch((): undefined => undefined);
        for (const l of res?.datos ?? []) {
          if (l.deveui) mapa.set(l.deveui.toUpperCase(), l);
        }
      }
      return mapa;
    },
  });

  private readonly usuario = resource({
    params: () => this.aplicacion()?.idUsuario ?? '',
    loader: async ({ params }): Promise<IUsuario | undefined> => {
      if (!params) return undefined;
      return (await this.nombres.usuarios([params])).get(params);
    },
  });

  protected readonly quien = computed(() =>
    nombreUsuario(this.usuario.value()),
  );

  protected readonly titulo = computed(() => {
    if (this.sinMarca()) {
      const cliente = this.nombres.nombreCliente(clienteSinMarca(this.id()));
      return cliente
        ? `Configuraciones pendientes de antes del seguimiento · ${cliente}`
        : 'Configuraciones pendientes de antes del seguimiento';
    }
    const a = this.aplicacion();
    return a ? queSeHizo(a) : undefined;
  });

  protected readonly motivo = computed(() => {
    const a = this.aplicacion();
    return a ? MOTIVOS_APLICACION[a.motivo] : undefined;
  });

  protected readonly fuente = computed(() => textoFuente(this.aplicacion()));
  protected readonly rutaFuente = computed(() => rutaFuente(this.aplicacion()));

  /** Por qué falta cada luminaria que no tiene el perfil. */
  protected readonly faltan = computed<Falta[]>(() => {
    const ahora = Date.now();
    const ultimos = this.ultimos.value();
    const lums = this.luminarias.value();
    const out: Falta[] = [];
    for (const cd of this.lista()) {
      const ultimo = cd.idEntidad ? ultimos?.get(cd.idEntidad) : undefined;
      const motivo = motivoFalta(cd, ultimo, ahora);
      if (!motivo) continue;
      const deveui = cd.dispositivo?.deveui;
      out.push({
        cd,
        motivo,
        deveui,
        idDispositivo: cd.dispositivo?._id ?? cd.idEntidad,
        luminaria: deveui ? lums?.get(deveui.toUpperCase()) : undefined,
        diferencias: explicarDiferencias(cd.diffs),
        ultimo,
      });
    }
    return out.sort((a, b) => ordenFalta(a.motivo) - ordenFalta(b.motivo));
  });

  protected readonly total = computed(() => this.lista().length);
  protected readonly aplicadas = computed(
    () => this.total() - this.faltan().length,
  );

  protected readonly porcentaje = computed(() =>
    this.total() ? Math.floor((this.aplicadas() / this.total()) * 100) : 0,
  );

  private readonly cuenta = computed(() => {
    const c: Record<MotivoFalta, number> = {
      detenido: 0,
      'no-aplicado': 0,
      'sin-comunicacion': 0,
      'en-curso': 0,
    };
    for (const f of this.faltan()) c[f.motivo]++;
    return c;
  });

  protected readonly aRevisar = computed(
    () => this.cuenta().detenido + this.cuenta()['no-aplicado'],
  );
  protected readonly sinComunicacion = computed(
    () => this.cuenta()['sin-comunicacion'],
  );

  protected readonly estado = computed(() => {
    const c = this.cuenta();
    return estadoAplicacion({
      aplicado: this.aplicadas(),
      enCurso: c['en-curso'] + c['sin-comunicacion'] + c['no-aplicado'],
      detenido: c.detenido,
    });
  });

  protected readonly segmentos = computed(() => {
    const total = this.total();
    if (!total) return [];
    const c = this.cuenta();
    return [
      {
        clave: 'aplicado',
        etiqueta: 'Con el perfil',
        valor: this.aplicadas(),
        tono: TONO.ok,
      },
      ...(
        [
          'en-curso',
          'no-aplicado',
          'detenido',
          'sin-comunicacion',
        ] as MotivoFalta[]
      ).map((m) => ({
        clave: m,
        etiqueta: MOTIVOS_FALTA[m].etiqueta,
        valor: c[m],
        tono: MOTIVOS_FALTA[m].tono,
      })),
    ]
      .filter((s) => s.valor)
      .map((s) => ({ ...s, ancho: (s.valor / total) * 100 }));
  });

  protected readonly porMotivo = computed(() =>
    (Object.entries(this.cuenta()) as [MotivoFalta, number][])
      .filter(([, n]) => n)
      .map(([motivo, cuantos]) => ({
        motivo,
        cuantos,
        ...MOTIVOS_FALTA[motivo],
      }))
      .sort((a, b) => ordenFalta(a.motivo) - ordenFalta(b.motivo)),
  );

  /** Cuánto lleva desde que arrancó, o cuánto tardó si ya terminó. */
  protected readonly lleva = computed(() => {
    const a = this.aplicacion();
    if (!a) return undefined;
    const desde = new Date(a.fecha).getTime();
    const ultima = this.lista()
      .map((cd) =>
        cd.fechaAplicacion ? new Date(cd.fechaAplicacion).getTime() : NaN,
      )
      .filter((t) => !Number.isNaN(t) && t >= desde)
      .sort((x, y) => y - x)[0];
    const hasta = this.faltan().length ? Date.now() : ultima;
    if (Number.isNaN(desde) || !hasta || hasta < desde) return undefined;
    return duracion(hasta - desde);
  });

  /** Epoch ms de la acción; `undefined` en las pendientes sin marca. */
  private readonly inicio = computed(() => {
    const a = this.aplicacion();
    const t = a ? new Date(a.fecha).getTime() : NaN;
    return Number.isNaN(t) ? undefined : t;
  });

  /** Cuándo quedó con el perfil cada luminaria, de la primera a la última. */
  private readonly momentos = computed(() => {
    const a = this.aplicacion();
    return a ? momentosLlegada(this.lista(), a.fecha) : [];
  });

  /** Hasta dónde se dibuja el tiempo: ahora si falta alguna, la última llegada si no. */
  private readonly fin = computed(() => {
    const m = this.momentos();
    return this.faltan().length
      ? Date.now()
      : (m[m.length - 1] ?? this.inicio() ?? Date.now());
  });

  /** Cuánto tardó cada porcentaje de las luminarias en quedar con el perfil. */
  protected readonly hitos = computed(() => {
    const inicio = this.inicio();
    if (inicio === undefined || !this.total()) return [];
    const total = this.total();
    const llegaron = this.momentos().length;
    const n = (x: number) => x.toLocaleString('es-AR');
    return hitosConvergencia(this.momentos(), total, inicio).map((h) => ({
      ...h,
      faltan: Math.max(0, h.necesarias - llegaron),
      ayuda:
        h.porcentaje === 100
          ? 'Cuánto tardó la última luminaria en quedar con el perfil.'
          : `Cuánto tardaron en quedar con el perfil ${n(h.necesarias)} de ${n(total)} luminarias (el ${h.porcentaje} %).`,
      demoraTexto: h.demora === undefined ? undefined : duracion(h.demora),
      cuando:
        h.momento === undefined ? undefined : fechaHora(new Date(h.momento)),
    }));
  });

  protected readonly grafico = computed<Highcharts.Options | undefined>(() => {
    const a = this.aplicacion();
    const inicio = this.inicio();
    const total = this.total();
    if (!a || inicio === undefined || !total) return undefined;
    const puntos = curvaAvance(this.lista(), a.fecha);
    // La curva llega hasta ahora aunque no haya novedades: si quedó plana, se ve.
    if (this.faltan().length) {
      puntos.push([Date.now(), puntos[puntos.length - 1][1]]);
    }
    const tenue = ChartColors.subtitleColor() || '#666666';
    // Los hitos de 50 y 90 %: dónde llegó la mitad y dónde empieza la punta lenta.
    const marcas = this.hitos()
      .filter(
        (h) =>
          (h.porcentaje === 50 || h.porcentaje === 90) &&
          h.momento !== undefined,
      )
      .map(
        (h, i): Highcharts.XAxisPlotLinesOptions => ({
          value: h.momento,
          color: tenue,
          dashStyle: 'Dash',
          width: 1,
          zIndex: 3,
          label: {
            text: `${h.porcentaje} %`,
            rotation: 0,
            align: 'left',
            x: 4,
            y: 14 + i * 14,
            style: { color: tenue, fontSize: '11px' },
          },
        }),
      );
    return {
      chart: { type: 'line', zooming: { type: 'x' } },
      time: { useUTC: false },
      xAxis: {
        type: 'datetime',
        dateTimeLabelFormats: FORMATOS_FECHA_EJE,
        crosshair: true,
        plotLines: marcas,
      },
      yAxis: {
        title: { text: undefined },
        min: 0,
        max: total,
        allowDecimals: false,
      },
      series: [
        {
          type: 'line',
          name: 'Con el perfil',
          data: puntos,
          step: 'left',
          color: ChartColors.color1(),
          marker: { enabled: puntos.length < 40 },
        },
      ],
      legend: { enabled: false },
      tooltip: {
        xDateFormat: '%d/%m %H:%M',
        pointFormatter: function () {
          const y = this.y ?? 0;
          const pct = Math.floor((y / total) * 100);
          const x = typeof this.x === 'number' ? this.x : inicio;
          return (
            `<b>${y.toLocaleString('es-AR')}</b> luminarias (${pct} %)<br/>` +
            `a ${x > inicio ? duracion(x - inicio) : '0 min'} de la acción`
          );
        },
      },
    };
  });

  /** Cuántas quedaron con el perfil en cada franja: el ritmo, y sus mesetas. */
  protected readonly graficoRitmo = computed<Highcharts.Options | undefined>(
    () => {
      const inicio = this.inicio();
      if (inicio === undefined || !this.momentos().length) return undefined;
      const { ancho, franjas } = ritmoLlegada(
        this.momentos(),
        inicio,
        this.fin(),
      );
      const anchoTexto = duracion(ancho);
      return {
        chart: { type: 'column', zooming: { type: 'x' } },
        time: { useUTC: false },
        xAxis: {
          type: 'datetime',
          dateTimeLabelFormats: FORMATOS_FECHA_EJE,
          crosshair: true,
        },
        yAxis: {
          title: { text: undefined },
          min: 0,
          allowDecimals: false,
          softMax: 1,
        },
        plotOptions: {
          column: {
            pointRange: ancho,
            pointPadding: 0.05,
            groupPadding: 0,
            borderRadius: 2,
          },
        },
        series: [
          {
            type: 'column',
            name: 'Llegaron',
            data: franjas,
            color: ChartColors.color1(),
          },
        ],
        legend: { enabled: false },
        tooltip: {
          xDateFormat: '%d/%m %H:%M',
          pointFormatter: function () {
            return `<b>${(this.y ?? 0).toLocaleString('es-AR')}</b> luminarias en ${anchoTexto}`;
          },
        },
      };
    },
  );

  /** Cuánto tardó cada luminaria, en tramos: si la mayoría fue rápida y unas pocas se estiraron. */
  protected readonly graficoDemoras = computed<Highcharts.Options | undefined>(
    () => {
      const inicio = this.inicio();
      if (inicio === undefined || !this.momentos().length) return undefined;
      const tramos = distribucionDemoras(this.momentos(), inicio);
      return {
        chart: { type: 'column' },
        xAxis: { categories: tramos.map((t) => t.etiqueta) },
        yAxis: {
          title: { text: undefined },
          min: 0,
          allowDecimals: false,
          maxPadding: 0.15,
          endOnTick: false,
        },
        plotOptions: { column: { borderRadius: 2 } },
        series: [
          {
            type: 'column',
            name: 'Luminarias',
            data: tramos.map((t) => t.cuantas),
            color: ChartColors.color1(),
            dataLabels: {
              enabled: true,
              inside: false,
              crop: false,
              overflow: 'allow',
              style: { fontWeight: '400' },
            },
          },
        ],
        legend: { enabled: false },
        tooltip: { valueSuffix: ' luminarias' },
      };
    },
  );

  /** Con y sin el perfil, por cliente, del que más luminarias tiene al que menos. */
  private readonly porCliente = computed(() => {
    const faltan = new Set(this.faltan().map((f) => f.cd._id));
    const porCliente = new Map<string, { con: number; sin: number }>();
    for (const cd of this.lista()) {
      const id = cd.idCliente ?? '';
      const c = porCliente.get(id) ?? { con: 0, sin: 0 };
      if (faltan.has(cd._id)) c.sin++;
      else c.con++;
      porCliente.set(id, c);
    }
    return [...porCliente.entries()]
      .map(([id, c]) => ({
        nombre: this.nombres.nombreCliente(id) || 'Sin cliente',
        ...c,
      }))
      .sort((a, b) => b.con + b.sin - (a.con + a.sin));
  });

  /** Con y sin el perfil, por cliente: sólo cuando la acción tocó más de uno. */
  protected readonly graficoClientes = computed<Highcharts.Options | undefined>(
    () => {
      const filas = this.porCliente();
      if (filas.length < 2) return undefined;
      return {
        chart: { type: 'bar', height: Math.max(200, filas.length * 36 + 80) },
        xAxis: { categories: filas.map((f) => f.nombre) },
        yAxis: {
          title: { text: undefined },
          min: 0,
          allowDecimals: false,
          reversedStacks: false,
        },
        plotOptions: { bar: { stacking: 'normal', borderRadius: 2 } },
        series: [
          {
            type: 'bar',
            name: 'Con el perfil',
            data: filas.map((f) => f.con),
            color: ChartColors.success(),
          },
          {
            type: 'bar',
            name: 'Faltan',
            data: filas.map((f) => f.sin),
            color: ChartColors.neutral(),
          },
        ],
        legend: { enabled: true },
        tooltip: { shared: true, valueSuffix: ' luminarias' },
      };
    },
  );

  protected readonly altoGraficoClientes = computed(
    () => `${Math.max(200, this.porCliente().length * 36 + 80)}px`,
  );

  private readonly filtrados = computed<Falta[]>(() => {
    const todos = this.faltan();
    const motivo = this.motivoFiltro();
    if (motivo) return todos.filter((f) => f.motivo === motivo);
    return this.verSinComunicacion()
      ? todos
      : todos.filter((f) => f.motivo !== 'sin-comunicacion');
  });

  protected readonly visibles = computed(() => {
    const lista = this.filtrados();
    return this.motivoFiltro() ? lista : lista.slice(0, TOPE_TABLA);
  });

  protected readonly cuantosFiltrados = computed(() => this.filtrados().length);

  protected readonly sinComunicacionOcultos = computed(() =>
    this.motivoFiltro() || this.verSinComunicacion()
      ? 0
      : this.cuenta()['sin-comunicacion'],
  );

  protected alternarMotivo(motivo: MotivoFalta): void {
    this.motivoFiltro.set(this.motivoFiltro() === motivo ? '' : motivo);
  }

  /**
   * El informe espera a que estén el último envío y el nombre de cada luminaria
   * que falta: sin ellos saldría con la tabla a medias.
   */
  protected readonly informeListo = computed(
    () =>
      this.deseadas.hasValue() &&
      !this.deseadas.isLoading() &&
      !this.ultimos.isLoading() &&
      !this.luminarias.isLoading(),
  );

  /**
   * Arma el informe en PDF con lo que el detalle ya tiene cargado y lo descarga.
   * El módulo del PDF (y jsPDF) se carga con `import()` recién al pedirlo.
   */
  protected async descargarInforme(): Promise<void> {
    if (this.generandoInforme() || !this.informeListo()) return;
    this.generandoInforme.set(true);
    try {
      const datos = this.datosInforme();
      const { descargarInformeAplicacion } =
        await import('./informe-aplicacion-pdf');
      await descargarInformeAplicacion(datos);
    } catch (error) {
      console.error('Error armando el informe de la aplicación:', error);
      this.helper.notifError(
        'No se pudo armar el informe PDF.',
        'Cerrar',
        true,
      );
    } finally {
      this.generandoInforme.set(false);
    }
  }

  /** Todo lo que muestra el detalle, en una foto: el PDF no depende de Angular. */
  private datosInforme(): DatosInformeAplicacion {
    const a = this.aplicacion();
    const usuario = LoginService.getUsuario();
    const cuenta = this.cuenta();
    const momentos = this.momentos();
    const inicio = this.inicio();
    const estado = this.estado();
    const fecha = (iso: string | undefined) => {
      const d = iso ? new Date(iso) : undefined;
      return d && !Number.isNaN(d.getTime()) ? d : undefined;
    };
    const porCliente = this.porCliente();
    const conCliente = porCliente.length > 1;
    const ahora = Date.now();

    const faltan = this.faltan();
    const filas = faltan.slice(0, TOPE_INFORME).map(
      (f): FilaFalta => ({
        luminaria: f.luminaria
          ? f.luminaria.identificacion || 'Sin identificación'
          : undefined,
        cliente: conCliente
          ? this.nombres.nombreCliente(f.cd.idCliente)
          : undefined,
        nodo: f.idDispositivo ? f.deveui || f.idDispositivo : undefined,
        motivo: f.motivo,
        detenidaHasta:
          f.motivo === 'detenido' && f.cd.bloqueadoHasta
            ? fecha(f.cd.bloqueadoHasta)
            : undefined,
        intentos: f.cd.reintentosReconciliacion || undefined,
        queFalta: f.diferencias.items,
        desconocida: f.diferencias.desconocida,
        sinComparar: !f.cd.ultimaComparacion,
        ultimoEnvio: f.ultimo
          ? {
              nombre: f.ultimo.nombre || 'Envío',
              estado: f.ultimo.estado || 'sin estado',
              fecha: fecha(f.ultimo.fechaCreacion),
              error: f.ultimo.ultimoError || undefined,
            }
          : undefined,
        ultimaComunicacion: fecha(f.cd.dispositivo?.fechaUltimaComunicacion),
      }),
    );

    let ritmo: DatosInformeAplicacion['ritmo'];
    if (inicio !== undefined && momentos.length) {
      const r = ritmoLlegada(momentos, inicio, this.fin());
      ritmo = {
        anchoMs: r.ancho,
        anchoTexto: duracion(r.ancho),
        franjas: r.franjas,
      };
    }

    const curva = a ? curvaAvance(this.lista(), a.fecha) : [];
    if (curva.length && faltan.length) {
      curva.push([ahora, curva[curva.length - 1][1]]);
    }

    const cliente = LoginService.getCliente();
    return {
      titulo: this.titulo() ?? 'Aplicación de perfil',
      cliente: cliente?.nombre,
      usuario: usuario?.datosPersonales?.nombre?.trim() || usuario?.usuario,
      generado: new Date(ahora),
      sinMarca: this.sinMarca(),
      accion: a
        ? {
            id: a.id,
            fecha: new Date(a.fecha),
            motivo: MOTIVOS_APLICACION[a.motivo]?.etiqueta ?? a.motivo,
            motivoDetalle: MOTIVOS_APLICACION[a.motivo]?.detalle ?? '',
            fuente: this.fuente(),
            perfil: a.nombrePerfil,
            quien: this.quien(),
          }
        : undefined,
      estado: {
        titulo: estado.titulo,
        terminada: estado.terminada,
        tono:
          estado.titulo === 'Aplicado'
            ? 'ok'
            : cuenta.detenido
              ? 'aviso'
              : 'info',
      },
      total: this.total(),
      aplicadas: this.aplicadas(),
      porcentaje: this.porcentaje(),
      aRevisar: this.aRevisar(),
      lleva: this.lleva(),
      primeraLlegada: momentos.length ? new Date(momentos[0]) : undefined,
      ultimaLlegada: momentos.length
        ? new Date(momentos[momentos.length - 1])
        : undefined,
      cuenta: { ...cuenta },
      hitos: this.hitos().map((h) => ({
        porcentaje: h.porcentaje,
        necesarias: h.necesarias,
        demora: h.demoraTexto,
        momento: h.momento === undefined ? undefined : new Date(h.momento),
        faltan: h.faltan,
      })),
      inicio,
      fin: this.fin(),
      curva,
      ritmo,
      demoras:
        inicio === undefined ? [] : distribucionDemoras(momentos, inicio),
      clientes: conCliente ? porCliente : [],
      faltan: filas,
      totalFaltan: faltan.length,
    };
  }

  protected cuando(iso: string): string {
    return haceCuanto(iso);
  }

  protected fecha(iso: string): string {
    return fechaHora(iso);
  }

  protected recargar(): void {
    this.deseadas.reload();
  }
}
